/**
 * components/UploadGlobalConf/EventManager.js
 * Gestion des événements et interactions utilisateur
 */

export class EventManager {
  constructor(parent) {
    this.parent = parent;
    this.currentMenuContainer = null;
  }

  /**
   * Gérer les événements d'upload
   */
  bindUploadEvents() {
    if (!this.parent.element) return;

    const button = this.parent.element.querySelector(".global-upload-btn");

    if (button) {
      // Supprimer les anciens listeners
      button.replaceWith(button.cloneNode(true));

      // Récupérer les nouveaux éléments
      const newButton = this.parent.element.querySelector(".global-upload-btn");

      // Appeler directement showDirectoryPicker au clic
      newButton.addEventListener("click", async () => {
        await this.parent.openDirectoryPicker();
      });
    }
  }

  /**
   * Empêcher les comportements par défaut pour le drag & drop
   */
  preventDefaults(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Bind événements de l'arbre
   */
  bindTreeEvents() {
    if (!this.parent.element) return;

    // Boutons d'assignation - CORRIGÉ
    this.parent.element.querySelectorAll(".tree-item-assign-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const itemName = btn.dataset.itemName;
        this.showCategoryMenu(btn, itemName);
      });
    });

    // Boutons d'exclusion - CORRIGÉ
    this.parent.element.querySelectorAll(".tree-item-exclude-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const itemName = btn.dataset.itemName;
        this.parent.excludeItem(itemName);
      });
    });

    // Boutons de libération - CORRIGÉ
    this.parent.element.querySelectorAll(".tree-item-unassign-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const itemName = btn.dataset.itemName;
        this.parent.unassignItem(itemName);
      });
    });

    // Selects des sous-nœuds
    this.parent.element.querySelectorAll(".subnode-select").forEach((select) => {
      select.addEventListener("change", (e) => {
        const itemName = e.target.dataset.itemName;
        const nodeType = e.target.dataset.nodeType;
        const value = e.target.value;

        if (value) {
          this.handleSubNodeSelection(itemName, nodeType, value);
        }
      });
    });
  }

  /**
   * Ferme le menu contextuel
   */
  closeCurrentMenu() {
    if (this.currentMenuContainer && document.body.contains(this.currentMenuContainer)) {
      document.body.removeChild(this.currentMenuContainer);
      document.removeEventListener("click", this.boundCloseMenu);
      this.currentMenuContainer = null;
    }
  }

  /**
   * Afficher le menu de catégories
   */
  showCategoryMenu(button, itemName) {
    // si menu affiché => fermer

    this.closeCurrentMenu();

    const item = this.parent.uploadedFiles.find((f) => f.name === itemName);

    const allCompatibleCategories = this.parent.getCompatibleCategories(item);

    // Filtrer les catégories déjà assignées
    const compatibleCategories = allCompatibleCategories.filter((cat) => {
      // Vérifier si cette catégorie est déjà assignée à un autre fichier
      const alreadyAssigned = Array.from(this.parent.assignedItems.values()).some(
        (assignedItem) => assignedItem.category === cat.key
      );
      return !alreadyAssigned;
    });

    // Créer le menu contextuel
    const menu = document.createElement("div");
    menu.className = "category-menu";

    // Si aucune catégorie disponible, afficher un message
    if (compatibleCategories.length === 0) {
      menu.innerHTML = `<div class="category-chips">
      <div class="no-categories">Toutes les catégories compatibles sont déjà assignées</div>
    </div>`;

      // Positionner le menu
      this.positionMenuWithClasses(button, menu);

      // Fermer le menu en cliquant ailleurs
      setTimeout(() => {
        const closeMenu = (e) => {
          if (
            this.currentMenuContainer &&
            !this.currentMenuContainer.contains(e.target) &&
            document.body.contains(this.currentMenuContainer)
          ) {
            document.body.removeChild(this.currentMenuContainer);
            this.currentMenuContainer = null;
            document.removeEventListener("click", closeMenu);
          }
        };
        this.boundCloseMenu = closeMenu;
        document.addEventListener("click", closeMenu);
      }, 200);

      return;
    }

    // Fonctions utilitaires pour le menu avec chips
    const getFormatBadge = (category) => {
      if (category.isFolder) return "[DOSSIER]";
      if (category.extensions.includes(".json")) return "[JSON]";
      if (category.extensions.some((ext) => [".xlsx", ".xls", ".xlsm"].includes(ext))) return "[XLSX]";
      return "";
    };

    const getCategoryColor = (category) => {
      if (category.key.includes("config")) return "config";
      if (category.key === "baseROP") return "base-rop";
      if (category.key === "rapportFiberPvReussite") return "pv-reussite";
      if (category.key === "pvReussite") return "pv-reussite";
      if (category.key === "rapportFiberPvEchec") return "pv-echec";
      if (category.key === "pvEchec") return "pv-echec";
      if (category.key === "pvHs") return "pv-hs";
      return "default";
    };

    // Générer le HTML des chips
    menu.innerHTML = `<div class="category-chips">${compatibleCategories
      .map((cat) => {
        const badge = getFormatBadge(cat);
        const colorClass = getCategoryColor(cat);
        return `<div class="category-chip ${colorClass}" data-category="${cat.key}">
        <span class="format-badge">${badge}</span>
        <span class="category-name">${cat.name}</span>
      </div>`;
      })
      .join("")}</div>`;

    // Positionner le menu intelligemment
    this.positionMenuWithClasses(button, menu);

    // Remplacer la section de positionnement par :
    menu.querySelectorAll(".category-chip").forEach((item) => {
      item.addEventListener("click", (e) => {
        const target = /** @type {HTMLElement} */ (e.currentTarget);
        const categoryKey = target.dataset.category;
        this.parent.assignItemToCategory(itemName, categoryKey);
        this.closeCurrentMenu();
      });
    });

    // Fermer le menu en cliquant ailleurs
    setTimeout(() => {
      const closeMenu = (e) => {
        if (
          this.currentMenuContainer &&
          !this.currentMenuContainer.contains(e.target) &&
          document.body.contains(this.currentMenuContainer)
        ) {
          document.body.removeChild(this.currentMenuContainer);
          this.currentMenuContainer = null;
          document.removeEventListener("click", closeMenu);
        }
      };
      document.addEventListener("click", closeMenu);
    }, 100);
  }

  /**
   * Positionner le menu avec les classes CSS
   */
  positionMenuWithClasses(button, menu) {
    const rect = button.getBoundingClientRect();

    // Créer le conteneur et ajouter le menu temporairement pour mesurer
    const menuContainer = document.createElement("div");

    // stocker la reference du menu container
    this.currentMenuContainer = menuContainer;

    menuContainer.style.position = "absolute";
    menuContainer.style.left = rect.left + "px";
    menuContainer.style.top = rect.top + "px";
    menuContainer.style.width = rect.width + "px";
    menuContainer.style.height = rect.height + "px";
    menuContainer.style.zIndex = "1000";
    menuContainer.style.pointerEvents = "none";

    menu.style.position = "absolute";
    menu.style.pointerEvents = "auto";
    menu.style.visibility = "hidden"; // Masquer temporairement

    menuContainer.appendChild(menu);
    document.body.appendChild(menuContainer);

    // Mesurer les dimensions réelles du menu
    const menuRect = menu.getBoundingClientRect();
    const menuWidth = menuRect.width;
    const menuHeight = menuRect.height;

    // Calculer l'espace disponible
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceRight = window.innerWidth - rect.right;

    // Déterminer la classe de positionnement
    let positionClass = "";
    if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
      // Pas assez d'espace en bas -> afficher AU DESSUS (position-bottom-*)
      positionClass = spaceRight < menuWidth ? "position-bottom-left" : "position-bottom-right";
    } else {
      // Assez d'espace en bas -> afficher EN DESSOUS (position-top-*)
      positionClass = spaceRight < menuWidth ? "position-top-left" : "position-top-right";
    }

    menu.className = `category-menu ${positionClass}`;
    menu.style.visibility = "visible"; // Réafficher le menu
  }

  /**
   * Gérer la sélection d'un sous-nœud
   */
  async handleSubNodeSelection(itemName, nodeType, value) {
    try {
      if (nodeType === "sheet") {
        await this.parent.processor.selectBaseRopSheet(itemName, value);
      } else if (nodeType === "column") {
        const column = this.parent.assignedItems
          .get(itemName)
          .subNodes.column.options.find((opt) => opt.name === value);
        await this.parent.processor.selectBaseRopColumn(itemName, column);
      }
    } catch (error) {
      this.parent.updateStatus("error", { message: error.message });
    }
  }
}
